import React, { useState, useEffect } from 'react';
import approvalsService, { PendingApproval } from '../../services/approvals';
import ImageCard from './ImageCard';
import ImageModal from '../modals/ImageModal';
import './ApprovalQueue.css';

// Notification types
type NotificationType = 'success' | 'error' | 'info';

interface Notification {
  type: NotificationType;
  message: string;
}

const ApprovalQueue: React.FC = () => {
  // Main data state
  const [approvals, setApprovals] = useState<PendingApproval[]>([]);
  const [selectedImage, setSelectedImage] = useState<PendingApproval | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Loading states - separate for different operations
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);

  // Notification state
  const [notification, setNotification] = useState<Notification | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    // Load from localStorage or default to 100
    const saved = localStorage.getItem('approvalQueuePageSize');
    return saved ? parseInt(saved, 10) : 100;
  });

  // Filters
  const [filters, setFilters] = useState({
    quality_tier: '',
    batch_id: '',
  });

  // Load approvals on mount and when filters/page/pageSize change
  useEffect(() => {
    loadApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters, pageSize]);

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Prevent default for our shortcuts
      const preventDefault = () => {
        e.preventDefault();
        e.stopPropagation();
      };

      switch(e.key) {
        case ' ': // Space - Approve selected
          if (selectedIds.size > 0 && !isBatchProcessing) {
            preventDefault();
            handleBatchApprove();
          }
          break;

        case 'x': // X - Reject selected
        case 'X':
          if (selectedIds.size > 0 && !isBatchProcessing) {
            preventDefault();
            handleBatchReject();
          }
          break;

        case 'a': // A - Select all
        case 'A':
          if (e.ctrlKey || e.metaKey) {
            preventDefault();
            selectAll();
          }
          break;

        case 'Escape': // ESC - Clear selection
          if (selectedIds.size > 0) {
            preventDefault();
            setSelectedIds(new Set());
          }
          if (selectedImage) {
            setSelectedImage(null);
          }
          break;

        case 'ArrowLeft': // Previous page
          if (!selectedImage && page > 1 && !isLoadingData) {
            preventDefault();
            setPage(p => p - 1);
          }
          break;

        case 'ArrowRight': // Next page
          if (!selectedImage && page < totalPages && !isLoadingData) {
            preventDefault();
            setPage(p => p + 1);
          }
          break;

        case 'r': // R - Refresh
        case 'R':
          if (!isLoadingData) {
            preventDefault();
            loadApprovals();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, isBatchProcessing, isLoadingData, page, totalPages, selectedImage]);

  // Auto-hide notifications after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (type: NotificationType, message: string) => {
    // Ensure message is always a string to prevent React Error #31
    const safeMessage = typeof message === 'string' ? message : String(message);
    setNotification({ type, message: safeMessage });
  };

  const loadApprovals = async () => {
    try {
      setIsLoadingData(true);
      const response = await approvalsService.getPendingApprovals(page, pageSize, filters);
      setApprovals(response.items);
      setTotalPages(Math.ceil(response.total / pageSize));

      // Clear selection when loading new data
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Failed to load approvals:', err);
      showNotification('error', 'Failed to load approvals. Please try again.');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleApprove = async (approval: PendingApproval, platforms: string[]) => {
    const approvalId = approval.id;

    try {
      // Add to processing set
      setProcessingIds(prev => new Set([...prev, approvalId]));

      await approvalsService.approve({
        approval_id: approvalId,
        platforms: platforms.length > 0 ? platforms : ['shutterstock', 'alamy', 'pond5'],
      });

      // Remove from list
      setApprovals(prev => prev.filter(a => a.id !== approvalId));

      // Clear selection if this was selected
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(approvalId);
        return newSet;
      });

      // Close modal if this was the selected image
      if (selectedImage?.id === approvalId) {
        setSelectedImage(null);
      }

      showNotification('success', `Image approved successfully for ${platforms.length} platform(s)`);

    } catch (err: any) {
      console.error('Failed to approve image:', err);
      const errorMsg = err?.response?.data?.detail || err.message || 'Failed to approve image';
      showNotification('error', errorMsg);
    } finally {
      // Remove from processing set
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(approvalId);
        return newSet;
      });
    }
  };

  const handleReject = async (approval: PendingApproval, reason: string) => {
    const approvalId = approval.id;

    try {
      // Add to processing set
      setProcessingIds(prev => new Set([...prev, approvalId]));

      const response = await approvalsService.reject({
        approval_id: approvalId,
        reason: reason || 'Quality not sufficient',
      });

      // Check if rejection was successful
      if (response && response.success !== false) {
        // Remove from list
        setApprovals(prev => prev.filter(a => a.id !== approvalId));

        // Clear selection if this was selected
        setSelectedIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(approvalId);
          return newSet;
        });

        // Close modal if this was the selected image
        if (selectedImage?.id === approvalId) {
          setSelectedImage(null);
        }

        showNotification('success', `Image rejected: ${reason}`);
      } else {
        // Safely extract error message, ensuring it's always a string
        let errorMsg = 'Failed to reject image';
        if (response?.error) {
          if (typeof response.error === 'string') {
            errorMsg = response.error;
          } else if (response.error?.message) {
            errorMsg = String(response.error.message);
          } else if (response.error?.msg) {
            errorMsg = String(response.error.msg);
          } else if (response.error?.detail) {
            errorMsg = String(response.error.detail);
          } else {
            // If error is an object, stringify it safely
            try {
              errorMsg = JSON.stringify(response.error);
            } catch {
              errorMsg = 'An unknown error occurred';
            }
          }
        }
        showNotification('error', errorMsg);
      }

    } catch (err: any) {
      console.error('Failed to reject image:', err);

      // Safely extract error message from various error formats
      let errorMsg = 'Failed to reject image';

      const errorDetail = err?.response?.data?.detail;
      if (errorDetail) {
        if (typeof errorDetail === 'string') {
          errorMsg = errorDetail;
        } else if (errorDetail?.msg) {
          errorMsg = String(errorDetail.msg);
        } else if (errorDetail?.message) {
          errorMsg = String(errorDetail.message);
        } else {
          // Try to stringify complex error objects
          try {
            errorMsg = JSON.stringify(errorDetail);
          } catch {
            errorMsg = err.message || 'Failed to reject image';
          }
        }
      } else if (err.message) {
        errorMsg = String(err.message);
      }

      showNotification('error', errorMsg);
    } finally {
      // Remove from processing set
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(approvalId);
        return newSet;
      });
    }
  };

  const handleBatchApprove = async () => {
    if (selectedIds.size === 0) {
      showNotification('info', 'Please select images to approve');
      return;
    }

    const confirmApprove = window.confirm(`Are you sure you want to approve ${selectedIds.size} images?`);
    if (!confirmApprove) return;

    try {
      setIsBatchProcessing(true);

      await approvalsService.batchApprove(
        Array.from(selectedIds),
        ['shutterstock', 'alamy', 'pond5']
      );

      // Remove approved items
      setApprovals(prev => prev.filter(a => !selectedIds.has(a.id)));

      const count = selectedIds.size;
      setSelectedIds(new Set());

      showNotification('success', `Successfully approved ${count} images`);

    } catch (err: any) {
      console.error('Failed to batch approve:', err);
      const errorMsg = err?.response?.data?.detail || err.message || 'Failed to batch approve';
      showNotification('error', errorMsg);
    } finally {
      setIsBatchProcessing(false);
    }
  };

  const handleBatchReject = async () => {
    if (selectedIds.size === 0) {
      showNotification('info', 'Please select images to reject');
      return;
    }

    const reason = window.prompt(
      `Enter rejection reason for ${selectedIds.size} images:`,
      'Quality not sufficient'
    );

    if (!reason) return;

    const confirmReject = window.confirm(`Are you sure you want to reject ${selectedIds.size} images with reason: "${reason}"?`);
    if (!confirmReject) return;

    try {
      setIsBatchProcessing(true);

      const response = await approvalsService.batchReject(
        Array.from(selectedIds),
        reason
      );

      if (response && response.success !== false) {
        // Remove rejected items
        setApprovals(prev => prev.filter(a => !selectedIds.has(a.id)));

        const count = selectedIds.size;
        setSelectedIds(new Set());

        showNotification('success', `Successfully rejected ${count} images`);
      } else {
        // Safely handle error message
        let errorMsg = 'Failed to batch reject';
        if (response?.error) {
          if (typeof response.error === 'string') {
            errorMsg = response.error;
          } else if (response.error?.message) {
            errorMsg = String(response.error.message);
          } else {
            errorMsg = 'Failed to batch reject images';
          }
        }
        showNotification('error', errorMsg);
      }

    } catch (err: any) {
      console.error('Failed to batch reject:', err);
      const errorMsg = err?.response?.data?.detail || err.message || 'Failed to batch reject';
      showNotification('error', errorMsg);
    } finally {
      setIsBatchProcessing(false);
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    if (selectedIds.size === approvals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(approvals.map(a => a.id)));
    }
  };

  return (
    <div className="approval-queue">
      {/* Notification Bar */}
      {notification && (
        <div className={`notification notification-${notification.type}`}>
          <span>{typeof notification.message === 'string' ? notification.message : String(notification.message)}</span>
          <button
            onClick={() => setNotification(null)}
            className="notification-close"
          >
            ×
          </button>
        </div>
      )}

      <div className="queue-header">
        <div>
          <h1>Approval Queue</h1>
          <p className="page-subtitle">
            Review and approve pending images - Click checkboxes to select multiple
            <span style={{ marginLeft: '1rem', fontSize: '0.875rem', color: '#666' }}>
              (Space=Approve, X=Reject, Ctrl+A=Select All, ←→=Navigate)
            </span>
          </p>
        </div>

        {selectedIds.size > 0 && (
          <div className="batch-actions">
            <span className="selection-count">{selectedIds.size} selected</span>
            <button
              onClick={handleBatchApprove}
              disabled={isBatchProcessing}
              className="btn btn-success"
            >
              {isBatchProcessing ? 'Processing...' : 'Approve Selected'}
            </button>
            <button
              onClick={handleBatchReject}
              disabled={isBatchProcessing}
              className="btn btn-danger"
            >
              {isBatchProcessing ? 'Processing...' : 'Reject Selected'}
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              disabled={isBatchProcessing}
              className="btn btn-ghost"
            >
              Clear Selection
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <div className="filter-group">
          <label>Quality Tier</label>
          <select
            value={filters.quality_tier}
            onChange={(e) => setFilters({ ...filters, quality_tier: e.target.value })}
            className="form-select"
            disabled={isLoadingData}
          >
            <option value="">All</option>
            <option value="premium">Premium (80+)</option>
            <option value="standard">Standard (60-79)</option>
            <option value="bulk">Bulk (&lt;60)</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Images Per Page</label>
          <select
            value={pageSize}
            onChange={(e) => {
              const newSize = parseInt(e.target.value, 10);
              setPageSize(newSize);
              localStorage.setItem('approvalQueuePageSize', newSize.toString());
              setPage(1); // Reset to first page when changing page size
            }}
            className="form-select"
            disabled={isLoadingData}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="250">250</option>
            <option value="500">500</option>
            <option value="1000">1000</option>
          </select>
        </div>

        <div className="filter-actions">
          <button
            onClick={selectAll}
            disabled={isLoadingData || isBatchProcessing}
            className="btn btn-ghost"
          >
            {selectedIds.size === approvals.length ? 'Deselect All' : 'Select All'}
          </button>
          <button
            onClick={loadApprovals}
            disabled={isLoadingData}
            className="btn btn-ghost"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path fillRule="evenodd" d="M8 3a5 5 0 104.546 2.914.5.5 0 01.908.417A6 6 0 118 2v1z"/>
              <path d="M8 4.466V.534a.25.25 0 01.41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 018 4.466z"/>
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoadingData && approvals.length === 0 ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading images...</p>
        </div>
      ) : approvals.length === 0 ? (
        <div className="empty-state">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <rect x="8" y="12" width="48" height="40" rx="4" stroke="currentColor" strokeWidth="2"/>
            <path d="M8 20h48M20 32h24M20 40h16" stroke="currentColor" strokeWidth="2" opacity="0.5"/>
          </svg>
          <h3>No pending approvals</h3>
          <p>All images have been reviewed</p>
        </div>
      ) : (
        <>
          {/* Image Grid */}
          <div className="image-grid">
            {approvals.map((approval) => (
              <ImageCard
                key={approval.id}
                approval={approval}
                selected={selectedIds.has(approval.id)}
                isProcessing={processingIds.has(approval.id)}
                disabled={isBatchProcessing}
                onSelect={() => toggleSelection(approval.id)}
                onApprove={(platforms) => handleApprove(approval, platforms)}
                onReject={(reason) => handleReject(approval, reason)}
                onView={() => setSelectedImage(approval)}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || isLoadingData}
                className="btn btn-ghost"
              >
                Previous
              </button>
              <span className="page-info">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || isLoadingData}
                className="btn btn-ghost"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <ImageModal
          approval={selectedImage}
          onClose={() => setSelectedImage(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onNext={() => {
            const currentIndex = approvals.findIndex(a => a.id === selectedImage.id);
            if (currentIndex < approvals.length - 1) {
              setSelectedImage(approvals[currentIndex + 1]);
            }
          }}
          onPrevious={() => {
            const currentIndex = approvals.findIndex(a => a.id === selectedImage.id);
            if (currentIndex > 0) {
              setSelectedImage(approvals[currentIndex - 1]);
            }
          }}
          hasNext={approvals.findIndex(a => a.id === selectedImage.id) < approvals.length - 1}
          hasPrevious={approvals.findIndex(a => a.id === selectedImage.id) > 0}
        />
      )}
    </div>
  );
};

export default ApprovalQueue;